import React, { useState } from 'react';
import TemplatePreviewsPanel from './TemplatePreviewsPanel';
import ColorField from './ColorField';
import TemplateVariablesField from './TemplateVariablesField';
import { PREVIEW_TEMPLATE_CONTEXT } from '../../utils/messageTemplate';
import { CARD_STYLES } from '../../utils/cardSurface';
import { CARD_PATTERNS, patternUsesGradient, GRADIENT_ANGLES } from '../../utils/cardPatterns';
import { getPatternSwatchStyle } from '../../utils/cardPatternStyle';
import { resolveTemplateColor } from '../../utils/templateColors';
import {
  NEXT_TEMPLATE_RULES,
  getAllowedNextTargets,
  getTransitionHint,
  wouldCreateCycle,
} from '../../utils/templateChain';

const TemplatesTab = ({
  templates, user, updateTemplate, onAdd, onDelete,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const template = templates[selectedIndex];

  const previewData = template ? {
    ...template,
    mainColor: resolveTemplateColor(template.mainColor, 'mainColor'),
    textColor: resolveTemplateColor(template.textColor, 'textColor'),
    labelColor: resolveTemplateColor(template.labelColor, 'labelColor'),
    cardStyle: template.cardStyle || 'plastic',
    backgroundPattern: template.backgroundPattern || 'solid',
    gradientColor: template.gradientColor || null,
    gradientAngle: template.gradientAngle ?? 135,
  } : null;

  const handleNextTemplateChange = (targetId) => {
    const fromId = template._id;
    if (targetId && wouldCreateCycle(templates, fromId, targetId)) {
      alert('Такой переход создаст цикл между уровнями. Выберите другой уровень.');
      return;
    }
    updateTemplate(selectedIndex, 'nextTemplateId', targetId || null);
  };

  if (!templates.length) {
    return (
      <div className="admin-section">
        <p className="admin-hint">Нет шаблонов.</p>
        <button type="button" className="admin-add-btn" onClick={onAdd}>+ Шаблон</button>
      </div>
    );
  }

  const allowedTargets = template ? getAllowedNextTargets(templates, template._id) : [];
  const transitionRule = template?.nextTemplateRule || 'amount';

  return (
    <div className="admin-section admin-templates-section">
      <div className="admin-row-between">
        <h2>Шаблоны карт</h2>
        <button type="button" className="admin-add-btn" onClick={onAdd}>+ Добавить</button>
      </div>

      <div className="admin-split-layout">
        <aside className="admin-sidebar">
          <p className="admin-sidebar-title">Уровни карт</p>
          <ul className="admin-template-list">
            {templates.map((t, i) => {
              const isFirst = i === 0;
              const hint = getTransitionHint(t, templates);
              return (
                <React.Fragment key={t._id}>
                  <li
                    className={`admin-template-list-item ${i === selectedIndex ? 'active' : ''} ${isFirst ? 'pinned' : ''}`}
                    onClick={() => setSelectedIndex(i)}
                  >
                    <span className={`admin-level-index ${isFirst ? 'pinned' : ''}`}>
                      {isFirst ? '★' : i + 1}
                    </span>
                    <span className="admin-template-list-name">{t.name || `Уровень ${i + 1}`}</span>
                    {isFirst && <span className="admin-badge">старт</span>}
                  </li>
                  {hint && (
                    <li className="admin-template-transition-arrow">
                      <span className="admin-template-arrow-icon">↓</span>
                      <span className="admin-template-arrow-text">
                        {hint.label}
                        <span className="admin-template-arrow-target">→ {hint.nextName}</span>
                      </span>
                    </li>
                  )}
                </React.Fragment>
              );
            })}
          </ul>
        </aside>

        {template && previewData && (
          <div className="admin-template-body">
            <div className="admin-template-editor">
              <div className="admin-template-settings">
                <div className="admin-card-group admin-template-colors">
                  <h3>Открытая карта</h3>
                  <p className="admin-hint admin-hint-inline">Цвет и узор — при нажатии на карту в кошельке</p>

                  <div className="admin-colors-grid admin-colors-grid-vertical">
                    <ColorField
                      label="Цвет фона"
                      value={previewData.mainColor}
                      onChange={(v) => updateTemplate(selectedIndex, 'mainColor', v)}
                    />
                    <ColorField
                      label="Текст"
                      value={previewData.textColor}
                      onChange={(v) => updateTemplate(selectedIndex, 'textColor', v)}
                    />
                    <ColorField
                      label="Подписи"
                      value={previewData.labelColor}
                      onChange={(v) => updateTemplate(selectedIndex, 'labelColor', v)}
                    />
                  </div>

                  <div className="admin-style-chips">
                    <span className="admin-color-label">Узор фона</span>
                    <div className="admin-pattern-grid">
                      {CARD_PATTERNS.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className={`admin-pattern-chip ${previewData.backgroundPattern === p.id ? 'active' : ''}`}
                          onClick={() => updateTemplate(selectedIndex, 'backgroundPattern', p.id)}
                          title={p.label}
                        >
                          <span
                            className={`admin-pattern-swatch card-bg-pattern--${p.id}`}
                            style={getPatternSwatchStyle({
                              mainColor: previewData.mainColor,
                              backgroundPattern: p.id,
                              gradientColor: previewData.gradientColor,
                              gradientAngle: previewData.gradientAngle,
                            })}
                          />
                          <span className="admin-pattern-name">{p.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {patternUsesGradient(previewData.backgroundPattern) && (
                    <div className="admin-gradient-settings">
                      <ColorField
                        label="Второй цвет"
                        value={previewData.gradientColor || previewData.mainColor}
                        onChange={(v) => updateTemplate(selectedIndex, 'gradientColor', v)}
                      />
                      <label className="admin-field">
                        <span>Направление градиента</span>
                        <select
                          value={previewData.gradientAngle ?? 135}
                          onChange={(e) => updateTemplate(selectedIndex, 'gradientAngle', Number(e.target.value))}
                        >
                          {GRADIENT_ANGLES.map((a) => (
                            <option key={a.value} value={a.value}>{a.label} {a.value}°</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}

                  <div className="admin-style-chips">
                    <span className="admin-color-label">Стиль карты</span>
                    <div className="admin-day-chips">
                      {CARD_STYLES.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className={`admin-day-chip ${previewData.cardStyle === s.id ? 'active' : ''}`}
                          onClick={() => updateTemplate(selectedIndex, 'cardStyle', s.id)}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="admin-card-group">
                  <h3>Параметры</h3>
                  <div className="admin-form-compact">
                    <label className="admin-field">
                      <span>Название</span>
                      <input type="text" value={template.name || ''} onChange={(e) => updateTemplate(selectedIndex, 'name', e.target.value)} />
                    </label>

                    {selectedIndex === 0 && (
                      <p className="admin-hint admin-hint-inline">
                        Первый уровень — стартовый (выдаётся при регистрации)
                      </p>
                    )}

                    <div className="admin-grid-2">
                      <label className="admin-field">
                        <span>Кэшбэк %</span>
                        <input type="number" min="0" max="100" value={template.cashback ?? 0} onChange={(e) => updateTemplate(selectedIndex, 'cashback', Number(e.target.value))} />
                      </label>
                      <label className="admin-field">
                        <span>Макс. списание %</span>
                        <input type="number" min="0" max="100" value={template.maximumBonusWriteOff ?? 100} onChange={(e) => updateTemplate(selectedIndex, 'maximumBonusWriteOff', Number(e.target.value))} />
                      </label>
                      <label className="admin-field">
                        <span>Срок бонусов, дн.</span>
                        <input type="number" min="0" placeholder="∞" value={template.bonusExpiresDays ?? ''} onChange={(e) => updateTemplate(selectedIndex, 'bonusExpiresDays', e.target.value ? Number(e.target.value) : null)} />
                      </label>
                    </div>

                    {templates.length > 1 && (
                      <div className="admin-template-transition-settings">
                        <h4 className="admin-subheading">Переход на другой уровень</h4>
                        <label className="admin-field">
                          <span>Целевой уровень</span>
                          <select
                            value={template.nextTemplateId || ''}
                            onChange={(e) => handleNextTemplateChange(e.target.value || null)}
                          >
                            <option value="">— не переходить —</option>
                            {allowedTargets.map((t) => (
                              <option key={t._id} value={t._id}>
                                {t.name || 'Уровень'}
                              </option>
                            ))}
                          </select>
                        </label>

                        {template.nextTemplateId && (
                          <>
                            <label className="admin-field">
                              <span>Условие перехода</span>
                              <select
                                value={transitionRule}
                                onChange={(e) => updateTemplate(selectedIndex, 'nextTemplateRule', e.target.value)}
                              >
                                {NEXT_TEMPLATE_RULES.map((r) => (
                                  <option key={r.id} value={r.id}>{r.label}</option>
                                ))}
                              </select>
                            </label>

                            {transitionRule === 'purchases' ? (
                              <label className="admin-field">
                                <span>Количество покупок</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={template.nextTemplatePurchaseCount ?? 0}
                                  onChange={(e) => updateTemplate(selectedIndex, 'nextTemplatePurchaseCount', Number(e.target.value))}
                                />
                              </label>
                            ) : (
                              <label className="admin-field">
                                <span>Порог суммы покупок, ₽</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={template.nextTemplateAmount ?? 0}
                                  onChange={(e) => updateTemplate(selectedIndex, 'nextTemplateAmount', Number(e.target.value))}
                                />
                              </label>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    <label className="admin-field admin-checkbox">
                      <input type="checkbox" checked={template.chargeOnWriteOff !== false} onChange={(e) => updateTemplate(selectedIndex, 'chargeOnWriteOff', e.target.checked)} />
                      <span>Начислять при списании</span>
                    </label>
                  </div>
                </div>

                <div className="admin-card-group">
                  <h3>Текст на обороте</h3>
                  <TemplateVariablesField
                    label="Описание / ссылки"
                    rows={4}
                    placeholder="Здравствуйте, {{имя}}! Ваш баланс: {{баланс}} бонусов."
                    value={template.backText || ''}
                    onChange={(v) => updateTemplate(selectedIndex, 'backText', v)}
                    previewContext={{
                      ...PREVIEW_TEMPLATE_CONTEXT,
                      уровень: previewData.name || 'Стандарт',
                      кэшбэк: `${previewData.cashback ?? 0}%`,
                      магазин: user.accountName || 'Магазин',
                    }}
                  />
                  {templates.length > 1 && selectedIndex > 0 && (
                    <button type="button" className="admin-delete-btn" onClick={() => onDelete(template._id)}>Удалить шаблон</button>
                  )}
                </div>
              </div>

              <TemplatePreviewsPanel
                key={`${previewData._id}-${previewData.mainColor}-${previewData.backgroundPattern}-${previewData.gradientColor}-${previewData.gradientAngle}-${previewData.cardStyle}`}
                user={user}
                template={previewData}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatesTab;
